import React, { useEffect, useRef, useState } from "react";

import {
    NativeSyntheticEvent,
    Platform,
    TextInputChangeEventData,
    TouchableOpacity,
    View,
    Text
} from "react-native";
import AppTheme from "../../../utils/Theme";
import TextInput from "../../../designSystem/TextInput/RoundCornerTextInput";
import Divider from "../../../designSystem/Divider/Divider";
import RoundButton from "../../../designSystem/Buttons/RoundButton";
import LinkButton from "../../../designSystem/Buttons/LinkButton";
import i18n from "../../../utils/Localization/Localization";
import { validateEmail } from "../../../utils/StringUtils";
import { signInWithEmailAndPassword } from "../../../services/Auth";
import { relaunchApp } from "../../../../specific/utils/Navigation/Navigation";
import ComponentProps from "../../../../specific/utils/Interfaces/ComponentProps";
import { useNavigate } from "../../../../specific/utils/Navigation/Navigation";
import Alert from "../../Notification/Alert";
import TNEAuth from "../../../../specific/components/Authentication/TNE/Auth";
import Modal from "../../../../specific/components/Modal/Modal";
import SVGView from "../../../utils/SvgView";
import { default as BtnGar } from "../../../assets/svg/illustrations/btn_gar.svg";

interface SignInProps extends ComponentProps {
    onChangePasswordRequest: () => void,
    userEmail?: string
}

const emptyErrors = {
    email: "",
    password: ""
};

const SignIn = (props: SignInProps) => {

    const appTheme: AppTheme = new AppTheme();

    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState(emptyErrors);
    const [showTneAuth, setShowTneAuth] = useState<boolean>(false);

    const alertRef = useRef<Alert>(null);

    const { componentId = "", userEmail = "", onChangePasswordRequest } = props;
    const [email, setEmail] = useState("");

    let navigate = "";
    if (Platform.OS === "web") {
        navigate = useNavigate();
    }

    const connectUser = async () => {
        try {
            const signInResponse = await signInWithEmailAndPassword({ email, password });
            setIsLoading(false);
            if (Platform.OS === "web") {
                relaunchApp({
                    componentId,
                    navigate
                });
            }
        } catch (error) {
            setIsLoading(false);
            let title = "Une erreur est survenue";
            let body = "Veuillez réessayer plus tard s'il vous plaît.\nSi l'erreur persiste, n'hésitez pas à nous contacter : support@lili.cool";
            if (error.toString().indexOf("auth/user-disabled") !== -1) {
                title = "Compte désactivé";
                body = "Votre compte a été désactivé.\nN'hésitez pas à nous contacter : support@lili.cool";
            } else if ((error.toString().indexOf("auth/invalid-credential") !== -1) || (error.toString().indexOf("auth/wrong-password") !== -1) || (error.toString().indexOf("auth/user-not-found") !== -1)) {
                title = "Adresse e-mail ou mot de passe erronée";
                body = "Veuillez vérifier votre adresse e-mail et votre mot de passe s'il vous plaît.\nSi vous avez oublié votre mot de passe, vous pouvez le retrouver via le lien \"Mot de passe oublié\"";
            } else if (error.toString().indexOf("auth/network-request-failed") !== -1) {
                title = "Erreur de connexion 📡";
                body = "Vérifiez votre connexion internet pour vous connecter à votre compte s'il vous plaît."
            }
            setErrors({ email: title, password: body });
        }
    }

    const onSignInButtonPressed = () => {
        setIsLoading(true);
        let signInErrors = { email: "", password: "" };
        if (email.length === 0) {
            signInErrors.email = "Votre e-mail ne peut être vide";
        } else {
            const isEmailValid = validateEmail(email);
            if (isEmailValid === false) {
                signInErrors.email = "La saisie de votre e-mail est incorrect";
            }
        }
        if (password.length === 0) {
            signInErrors.password = "Votre mot de passe ne peut être vide";
        } else if (password.length < 6) {
            signInErrors.password = "Votre mot de passe doit contenir 6 caractères minimum";
        }

        if ((signInErrors.email.length > 0) || (signInErrors.password.length > 0)) {
            setErrors(signInErrors);
            setIsLoading(false);
        } else {
            //Sign In
            connectUser();
        }
    }

    const onChangeText = (text: string) => {
        setEmail(text);
    }

    return (<View style={{ justifyContent: "center", alignItems: "center", width: appTheme.getBlockWidth() }}>
        <TextInput value={email} onFocus={() => setErrors(emptyErrors)} error={errors.email} onChangeText={onChangeText} type="email" placeholder={i18n.t("authentication.signIn.form.emailPlaceholder")} />
        <TextInput onFocus={() => setErrors(emptyErrors)} error={errors.password} onChangeText={(text) => setPassword(text)} type="password" legend={i18n.t("authentication.signIn.form.passwordLegend")} placeholder={i18n.t("authentication.signIn.form.passwordPlaceholder")} />
        <Divider size={20} />
        <RoundButton isLoading={isLoading} onPress={onSignInButtonPressed} title={i18n.t("authentication.signIn.buttons.signIn")} />
        <Divider size={20} />
        <LinkButton onPress={onChangePasswordRequest} title={i18n.t("authentication.signIn.buttons.forgotPassword")} />
        <TNEAuth onSuccess={() => relaunchApp({
            componentId,
            navigate
        })} />
    </View>)

}

export default SignIn;